import { useEffect, useRef, useState } from "react";
import "./App.css";

const STORAGE_KEY = "checkin_auth_payload";
const USER_ENDPOINTS = ["/api/user", "/api/profile/me"];
const ATTENDANCE_TODAY_ENDPOINT = "/api/attendance/today";
const ATTENDANCE_CHECKIN_ENDPOINT = "/api/attendance/checkin/start";
const ATTENDANCE_CHECKOUT_ENDPOINT = "/api/attendance/checkin/end";
const IMAGE_UPLOAD_ENDPOINT = "/api/images/upload";
const UNAUTHORIZED_STATUSES = new Set([401, 419]);
const OFFICE_LOCATION = {
  lat: 21.0092023,
  lng: 105.8235616,
  label: "So 1 Thai Ha, Trung Liet, Dong Da, Ha Noi"
};

function getInitialRoute() {
  if (window.location.pathname === "/profile") {
    return "profile";
  }

  if (window.location.pathname === "/checkin") {
    return "checkin";
  }

  return "login";
}

function App() {
  const [route, setRoute] = useState(getInitialRoute);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [auth, setAuth] = useState(null);
  const [me, setMe] = useState(null);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceForm, setAttendanceForm] = useState({
    time: "",
    ip_address: "222.252.22.40",
    gps_location: formatGpsLocation(generateNearbyGps()),
    ssid: "Yopaz Zone 3 5Ghz"
  });
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingMe, setIsLoadingMe] = useState(false);
  const [isLoadingToday, setIsLoadingToday] = useState(false);
  const [attendanceAction, setAttendanceAction] = useState(null);
  const [snapshotFile, setSnapshotFile] = useState(null);
  const [snapshotPreview, setSnapshotPreview] = useState("");
  const [isUploadingSnapshot, setIsUploadingSnapshot] = useState(false);
  const [message, setMessage] = useState(null);
  const snapshotInputRef = useRef(null);

  useEffect(() => {
    if (!snapshotFile) {
      setSnapshotPreview("");
      return;
    }

    const objectUrl = window.URL.createObjectURL(snapshotFile);
    setSnapshotPreview(objectUrl);

    return () => {
      window.URL.revokeObjectURL(objectUrl);
    };
  }, [snapshotFile]);

  useEffect(() => {
    let active = true;

    async function bootstrapSession() {
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
          if (active && getInitialRoute() === "profile") {
            window.history.replaceState({}, "", "/");
            setRoute("login");
          }
          return;
        }

        const parsed = JSON.parse(raw);
        if (!parsed?.access_token) {
          window.localStorage.removeItem(STORAGE_KEY);
          return;
        }

        const normalized = normalizeAuthPayload(parsed);
        const [userPayload, attendancePayload] = await Promise.all([
          fetchCurrentUser(normalized),
          fetchTodayAttendance(normalized)
        ]);

        if (!active) {
          return;
        }

        setAuth(normalized);
        setMe(userPayload);
        setTodayAttendance(attendancePayload);
        const nextRoute = getInitialRoute() === "profile" ? "profile" : "checkin";
        window.history.replaceState({}, "", nextRoute === "profile" ? "/profile" : "/checkin");
        setRoute(nextRoute);
      } catch (error) {
        window.localStorage.removeItem(STORAGE_KEY);
        if (active && UNAUTHORIZED_ERROR_CODES.has(error.code)) {
          window.history.replaceState({}, "", "/");
          setRoute("login");
        }
      } finally {
        if (active) {
          setIsBootstrapping(false);
        }
      }
    }

    bootstrapSession();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setRoute(getInitialRoute());
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (!isBootstrapping && (route === "profile" || route === "checkin") && !auth?.access_token) {
      window.history.replaceState({}, "", "/");
      setRoute("login");
    }
  }, [route, auth, isBootstrapping]);

  useEffect(() => {
    if (!isBootstrapping && auth?.access_token && me && route === "login") {
      window.history.replaceState({}, "", "/checkin");
      setRoute("checkin");
    }
  }, [auth, me, route, isBootstrapping]);

  useEffect(() => {
    if (isBootstrapping || !auth?.access_token) {
      setMe(null);
      return;
    }

    let active = true;

    async function fetchMe() {
      setIsLoadingMe(true);
      try {
        const payload = await fetchCurrentUser(auth);

        if (active) {
          setMe(payload);
        }
      } catch (error) {
        if (active) {
          setMe(null);
          if (UNAUTHORIZED_ERROR_CODES.has(error.code)) {
            clearSession();
          }
          setMessage({
            type: "error",
            text: error.message || "Khong the tai ho so nguoi dung."
          });
        }
      } finally {
        if (active) {
          setIsLoadingMe(false);
        }
      }
    }

    fetchMe();

    return () => {
      active = false;
    };
  }, [auth, isBootstrapping]);

  useEffect(() => {
    if (isBootstrapping || !auth?.access_token) {
      setTodayAttendance(null);
      return;
    }

    let active = true;

    async function loadTodayAttendance() {
      setIsLoadingToday(true);
      try {
        const payload = await fetchTodayAttendance(auth);

        if (active) {
          setTodayAttendance(payload);
        }
      } catch (error) {
        if (active) {
          setTodayAttendance(null);
          if (UNAUTHORIZED_ERROR_CODES.has(error.code)) {
            clearSession();
          }
          setMessage({
            type: "error",
            text: error.message || "Khong the tai thong tin check-in hom nay."
          });
        }
      } finally {
        if (active) {
          setIsLoadingToday(false);
        }
      }
    }

    loadTodayAttendance();

    return () => {
      active = false;
    };
  }, [auth, isBootstrapping]);

  function navigate(nextRoute) {
    const pathname =
      nextRoute === "profile" ? "/profile" : nextRoute === "checkin" ? "/checkin" : "/";
    window.history.pushState({}, "", pathname);
    setRoute(nextRoute);
  }

  function persistAuth(payload) {
    const nextAuth = normalizeAuthPayload(payload);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextAuth));
    setAuth(nextAuth);
  }

  function clearSession() {
    window.localStorage.removeItem(STORAGE_KEY);
    setAuth(null);
    setMe(null);
    setTodayAttendance(null);
    setPassword("");
    setSnapshotFile(null);
    if (getInitialRoute() !== "login") {
      window.history.replaceState({}, "", "/");
    }
    setRoute("login");
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSubmitting(true);
    setMessage(null);

    try {
      const response = await fetch("/api/login", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email: email.trim(),
          password
        })
      });

      const contentType = response.headers.get("content-type") || "";
      const payload = contentType.includes("application/json")
        ? await response.json()
        : await response.text();

      if (!response.ok) {
        const errorMessage =
          typeof payload === "string"
            ? payload
            : payload.message || payload.error || "Dang nhap that bai.";
        throw new Error(errorMessage);
      }

      persistAuth(payload);
      setPassword("");
      setMessage({ type: "success", text: "Dang nhap thanh cong." });
      navigate("checkin");
    } catch (error) {
      setMessage({
        type: "error",
        text: error.message || "Khong the dang nhap."
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleCopyToken() {
    if (!auth?.access_token) {
      return;
    }

    try {
      await navigator.clipboard.writeText(auth.access_token);
      setMessage({ type: "success", text: "Da copy access token." });
    } catch (_error) {
      setMessage({ type: "error", text: "Khong the copy token tren trinh duyet nay." });
    }
  }

  function handleLogout() {
    clearSession();
    setMessage({ type: "success", text: "Da dang xuat." });
  }

  async function handleRefreshProfile() {
    if (!auth?.access_token) {
      return;
    }

    setMessage(null);
    setAuth({ ...auth });
  }

  async function handleRefreshToday() {
    if (!auth?.access_token) {
      return;
    }

    setMessage(null);
    setAuth({ ...auth });
  }

  function handleAttendanceFieldChange(field, value) {
    setAttendanceForm((current) => ({
      ...current,
      [field]: value
    }));
  }

  async function handleAttendanceAction(type) {
    if (!auth?.access_token || attendanceAction) {
      return;
    }

    if (!snapshotFile) {
      setMessage({
        type: "error",
        text: "Ban can chup hoac chon anh khuon mat truoc khi cham cong."
      });
      return;
    }

    setAttendanceAction(type);
    setIsUploadingSnapshot(true);
    setMessage(null);

    try {
      const generatedGps = formatGpsLocation(generateNearbyGps());
      setAttendanceForm((current) => ({
        ...current,
        gps_location: generatedGps
      }));

      const uploadPayload = await uploadSnapshot(auth, snapshotFile);
      const snapshotPath = uploadPayload?.data?.path;

      if (!snapshotPath) {
        throw new Error("Upload anh thanh cong nhung khong nhan duoc path anh.");
      }

      const payload = await submitAttendanceAction(auth, type, {
        ...buildAttendancePayload({
          ...attendanceForm,
          gps_location: generatedGps
        }),
        camera_snapshot: snapshotPath
      });
      await handleRefreshToday();
      setSnapshotFile(null);
      setMessage({
        type: "success",
        text: payload?.message || (type === "checkin" ? "Check-in thanh cong." : "Check-out thanh cong.")
      });
    } catch (error) {
      if (UNAUTHORIZED_ERROR_CODES.has(error.code)) {
        clearSession();
      }
      setMessage({
        type: "error",
        text: error.message || "Khong the thuc hien cham cong."
      });
    } finally {
      setIsUploadingSnapshot(false);
      setAttendanceAction(null);
    }
  }

  function handleSnapshotSelection(event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setSnapshotFile(file);
    setMessage(null);
  }

  function handleClearSnapshot() {
    setSnapshotFile(null);
    if (snapshotInputRef.current) {
      snapshotInputRef.current.value = "";
    }
  }

  const isAuthenticated = Boolean(auth?.access_token && me);
  const activeRoute = isAuthenticated && route === "login" ? "checkin" : route;
  const isProfileRoute = isAuthenticated && activeRoute === "profile";
  const isCheckinRoute = isAuthenticated && activeRoute === "checkin";
  const attendanceData = todayAttendance?.data || {};
  const hasCheckedIn = Boolean(attendanceData.check_in_time);
  const hasCheckedOut = Boolean(attendanceData.check_out_time);

  if (isBootstrapping) {
    return (
      <main className="page-shell">
        <section className="hero-panel">
          <div className="intro-card">
            <p className="eyebrow">checkin.quan.pro.vn</p>
            <h1>Dang khoi phuc phien dang nhap</h1>
            <p className="lead">
              Frontend dang kiem tra token da luu bang <code>GET /api/user</code>.
            </p>
          </div>
          <div className="workspace-card">
            <div className="workspace-topbar">
              <span className="dot amber" />
              <span className="dot blue" />
              <span className="dot green" />
            </div>
            <div className="loading-state">Dang xac thuc phien hien tai...</div>
          </div>
        </section>
      </main>
    );
  }

  if (isCheckinRoute) {
    return (
      <main className="page-shell compact-shell">
        <section className="compact-checkin">
          {message ? <div className={`notice ${message.type}`}>{message.text}</div> : null}

          <AttendanceTodayCard attendance={todayAttendance} isLoading={isLoadingToday} compact />

          <section className="upload-panel">
            <div className="section-head compact">
              <div>
                <p className="panel-label">Anh khuon mat</p>
                <h3>{snapshotFile ? "Da chon anh" : "Chua co anh"}</h3>
              </div>
            </div>

            <div className="camera-preview compact-preview">
              {snapshotPreview ? (
                <img src={snapshotPreview} alt="Anh cham cong" className="snapshot-image" />
              ) : (
                <div className="camera-placeholder">
                  Bam nut ben duoi de chup anh tren dien thoai hoac chon anh co san.
                </div>
              )}
            </div>

            <input
              ref={snapshotInputRef}
              className="camera-input"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/*"
              capture="user"
              onChange={handleSnapshotSelection}
            />

            <div className="button-row compact-actions">
              <button
                className="secondary-button"
                type="button"
                onClick={() => snapshotInputRef.current?.click()}
                disabled={Boolean(attendanceAction) || isUploadingSnapshot}
              >
                {snapshotFile ? "Chon lai anh" : "Chon / Chup anh"}
              </button>
              <button
                className="secondary-button"
                type="button"
                onClick={handleClearSnapshot}
                disabled={!snapshotFile || Boolean(attendanceAction) || isUploadingSnapshot}
              >
                Xoa anh
              </button>
            </div>
          </section>

          <div className="button-row compact-actions">
            <button
              className="primary-button action-button"
              type="button"
              disabled={hasCheckedIn || attendanceAction === "checkin" || isLoadingToday || isUploadingSnapshot}
              onClick={() => handleAttendanceAction("checkin")}
            >
              {attendanceAction === "checkin" ? "Dang xu ly..." : hasCheckedIn ? "Da check-in" : "Check-in"}
            </button>
            <button
              className="secondary-button action-button"
              type="button"
              disabled={!hasCheckedIn || attendanceAction === "checkout" || isLoadingToday || isUploadingSnapshot}
              onClick={() => handleAttendanceAction("checkout")}
            >
              {attendanceAction === "checkout" ? "Dang xu ly..." : "Check-out"}
            </button>
          </div>

          <footer className="compact-footer">
            <p className="compact-user">{me?.name || me?.email || "Nguoi dung"}</p>
            <button className="ghost-button" type="button" onClick={handleLogout}>
              Dang xuat
            </button>
          </footer>
        </section>
      </main>
    );
  }

  return (
    <main className="page-shell">
      <section className="hero-panel">
        <div className="intro-card">
          <p className="eyebrow">checkin.quan.pro.vn</p>
          <h1>
            {isProfileRoute ? "Ho so nguoi dung" : isCheckinRoute ? "Check-in hom nay" : "Dang nhap lay Bearer token"}
          </h1>
          <p className="lead">
            Frontend React goi <code>/api/login</code> va lay ho so qua
            <code> /api/user</code>. Neu can, app se fallback sang
            <code> /api/profile/me</code>. Token duoc luu localStorage de giu
            phien lam viec tren cung domain.
          </p>

          <div className="feature-list">
            <article className="feature-item">
              <span className="feature-index">01</span>
              <p>Dang nhap bang email va password, sau do tu chuyen sang man hinh check-in.</p>
            </article>
            <article className="feature-item">
              <span className="feature-index">02</span>
              <p>Goi <code>/api/attendance/today</code> de hien thi thong tin check-in hom nay.</p>
            </article>
            <article className="feature-item">
              <span className="feature-index">03</span>
              <p>Giữ phiên theo ket qua API va co san man hinh ho so nguoi dung.</p>
            </article>
          </div>
        </div>

        <div className="workspace-card">
          <div className="workspace-topbar">
            <span className="dot amber" />
            <span className="dot blue" />
            <span className="dot green" />
          </div>

          {message ? (
            <div className={`notice ${message.type}`}>{message.text}</div>
          ) : null}

          {isProfileRoute ? (
            <section className="profile-screen">
              <div className="section-head">
                <div>
                  <p className="panel-label">Trang hien tai</p>
                  <h2>Thong tin tai khoan</h2>
                </div>
                <div className="nav-row">
                  <button className="ghost-button" type="button" onClick={() => navigate("checkin")}>
                    Xem check-in
                  </button>
                  <span className="status-badge">Da dang nhap</span>
                </div>
              </div>

              <div className="token-box">
                <span className="panel-label">Authorization</span>
                <code>{tokenType}</code>
                <textarea readOnly value={auth?.access_token || ""} />
              </div>

              <div className="button-row">
                <button className="primary-button" type="button" onClick={handleCopyToken}>
                  Copy token
                </button>
                <button className="secondary-button" type="button" onClick={handleRefreshProfile}>
                  Tai lai /user
                </button>
                <button className="secondary-button" type="button" onClick={handleLogout}>
                  Dang xuat
                </button>
              </div>

              <div className="profile-box">
                <div className="section-head compact">
                  <div>
                    <p className="panel-label">Du lieu API</p>
                    <h3>Thong tin co ban</h3>
                  </div>
                  {isLoadingMe ? <span className="status-badge">Dang tai...</span> : null}
                </div>

                {me ? <BasicProfile user={me} /> : <p className="empty-state">Chua co du lieu nguoi dung.</p>}
              </div>
            </section>
          ) : (
            <section className="login-screen">
              <div className="section-head">
                <div>
                  <p className="panel-label">Yopaz Auth</p>
                  <h2>Dang nhap</h2>
                </div>
                {auth?.access_token ? (
                  <button className="ghost-button" type="button" onClick={() => navigate("profile")}>
                    Mo ho so
                  </button>
                ) : null}
              </div>

              <form className="login-form" onSubmit={handleSubmit}>
                <label htmlFor="email">
                  Email
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="username"
                    placeholder="user@example.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </label>

                <label htmlFor="password">
                  Password
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="secret"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                </label>

                <button className="primary-button" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Dang xu ly..." : "Dang nhap"}
                </button>
              </form>

              <div className="request-box">
                <span className="panel-label">Endpoint</span>
                <code>POST /api/login</code>
                <p>
                  Yeu cau gui JSON voi <code>email</code> va <code>password</code>,
                  response ky vong tra ve <code>access_token</code> va
                  <code> token_type</code>.
                </p>
              </div>
            </section>
          )}
        </div>
      </section>
    </main>
  );
}

export default App;

function BasicProfile({ user }) {
  const items = [
    { label: "Ho va ten", value: user.name },
    { label: "Email", value: user.email || user.company_email },
    { label: "Chuc danh", value: user.job_title },
    { label: "Ma nhan vien", value: user.employee_code },
    { label: "Vi tri", value: user.position },
    { label: "Quan ly truc tiep", value: user.manager_name },
    { label: "So dien thoai", value: user.phone_number },
    { label: "Trang thai lam viec", value: user.work_status },
    { label: "Dang lam viec", value: user.is_currently_working ? "Co" : "Khong" },
    { label: "Dia chi hien tai", value: user.current_address },
    { label: "Gioi tinh", value: user.gender },
    { label: "Ngay sinh", value: user.birthdate }
  ].filter((item) => item.value);

  return (
    <div className="basic-profile">
      <div className="identity-card">
        {user.avatar ? (
          <img className="avatar" src={user.avatar} alt={user.name || "User avatar"} />
        ) : (
          <div className="avatar avatar-fallback">{getInitials(user.name)}</div>
        )}

        <div>
          <h4>{user.name || "Chua co ten"}</h4>
          <p>{user.job_title || "Chua co chuc danh"}</p>
          <span className="identity-meta">{user.company_email || user.email}</span>
        </div>
      </div>

      <div className="profile-grid">
        {items.map((item) => (
          <article className="info-card" key={item.label}>
            <span className="info-label">{item.label}</span>
            <strong>{String(item.value)}</strong>
          </article>
        ))}
      </div>
    </div>
  );
}

function AttendanceTodayCard({ attendance, isLoading }) {
  if (isLoading) {
    return <div className="attendance-card muted">Dang tai thong tin check-in hom nay...</div>;
  }

  const data = attendance?.data || {};
  const checkInTime = formatDateTime(data.check_in_time);
  const checkOutTime = formatDateTime(data.check_out_time);

  return (
    <section className="attendance-card">
      <div className="attendance-grid">
        <article className="info-card">
          <span className="info-label">Check-in</span>
          <strong>{checkInTime || "--:--"}</strong>
        </article>
        <article className="info-card">
          <span className="info-label">Check-out</span>
          <strong>{checkOutTime || "--:--"}</strong>
        </article>
      </div>
    </section>
  );
}

async function fetchCurrentUser(auth) {
  const authorization = `${auth.token_type || "Bearer"} ${auth.access_token}`;
  let lastError = null;

  for (const endpoint of USER_ENDPOINTS) {
    const response = await fetch(endpoint, {
      headers: {
        Accept: "application/json",
        Authorization: authorization
      }
    });

    const contentType = response.headers.get("content-type") || "";
    const payload = contentType.includes("application/json")
      ? await response.json()
      : await response.text();

    if (response.ok) {
      return payload;
    }

    const errorMessage =
      typeof payload === "string"
        ? payload
        : payload.message || payload.error || `Khong the lay thong tin tu ${endpoint}.`;

    if (UNAUTHORIZED_STATUSES.has(response.status)) {
      const error = new Error("Phien dang nhap khong con hop le. Vui long dang nhap lai.");
      error.code = "UNAUTHORIZED";
      throw error;
    }

    if (response.status !== 404) {
      throw new Error(errorMessage);
    }

    lastError = new Error(errorMessage);
  }

  throw lastError || new Error("Khong the lay thong tin nguoi dung.");
}

function normalizeAuthPayload(payload) {
  return {
    ...payload,
    token_type: payload.token_type || "Bearer"
  };
}

function getInitials(name) {
  if (!name) {
    return "U";
  }

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

async function fetchTodayAttendance(auth) {
  const response = await fetch(ATTENDANCE_TODAY_ENDPOINT, {
    headers: {
      Accept: "application/json",
      Authorization: `${auth.token_type || "Bearer"} ${auth.access_token}`
    }
  });

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (response.ok) {
    return payload;
  }

  const errorMessage =
    typeof payload === "string"
      ? payload
      : payload.message || payload.error || "Khong the lay thong tin check-in hom nay.";

  if (UNAUTHORIZED_STATUSES.has(response.status)) {
    const error = new Error("Phien dang nhap khong con hop le. Vui long dang nhap lai.");
    error.code = "UNAUTHORIZED";
    throw error;
  }

  throw new Error(errorMessage);
}

async function submitAttendanceAction(auth, type, body = {}) {
  const endpoint = type === "checkout" ? ATTENDANCE_CHECKOUT_ENDPOINT : ATTENDANCE_CHECKIN_ENDPOINT;
  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `${auth.token_type || "Bearer"} ${auth.access_token}`
    },
    body: JSON.stringify(body)
  });

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (response.ok) {
    return payload;
  }

  const errorMessage = extractErrorMessage(payload, "Khong the thuc hien cham cong.");

  if (UNAUTHORIZED_STATUSES.has(response.status)) {
    const error = new Error("Phien dang nhap khong con hop le. Vui long dang nhap lai.");
    error.code = "UNAUTHORIZED";
    throw error;
  }

  throw new Error(errorMessage);
}

async function uploadSnapshot(auth, file) {
  const formData = new FormData();
  formData.append("upload", file);
  formData.append("path", "attendance");

  const response = await fetch(IMAGE_UPLOAD_ENDPOINT, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `${auth.token_type || "Bearer"} ${auth.access_token}`
    },
    body: formData
  });

  const contentType = response.headers.get("content-type") || "";
  const payload = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (response.ok) {
    return payload;
  }

  const errorMessage = extractErrorMessage(payload, "Khong the upload anh cham cong.");

  if (UNAUTHORIZED_STATUSES.has(response.status)) {
    const error = new Error("Phien dang nhap khong con hop le. Vui long dang nhap lai.");
    error.code = "UNAUTHORIZED";
    throw error;
  }

  throw new Error(errorMessage);
}

function extractErrorMessage(payload, fallback) {
  if (typeof payload === "string") {
    return payload || fallback;
  }

  if (payload?.errors && typeof payload.errors === "object") {
    const firstError = Object.values(payload.errors).flat().find(Boolean);
    if (firstError) {
      return firstError;
    }
  }

  return payload?.message || payload?.error || fallback;
}

function formatDateTime(value) {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleTimeString("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  });
}

function buildAttendancePayload(form) {
  const payload = {};

  if (form.time) {
    payload.time = form.time.replace("T", " ") + ":00";
  }

  if (form.ip_address.trim()) {
    payload.ip_address = form.ip_address.trim();
  }

  if (form.gps_location.trim()) {
    payload.gps_location = form.gps_location.trim();
  }

  if (form.ssid.trim()) {
    payload.ssid = form.ssid.trim();
  }

  return payload;
}

function generateNearbyGps() {
  const radiusMeters = 10;
  const distance = Math.random() * radiusMeters;
  const angle = Math.random() * Math.PI * 2;
  const latOffset = (distance * Math.cos(angle)) / 111320;
  const lngOffset =
    (distance * Math.sin(angle)) /
    (111320 * Math.cos((OFFICE_LOCATION.lat * Math.PI) / 180));

  return {
    lat: OFFICE_LOCATION.lat + latOffset,
    lng: OFFICE_LOCATION.lng + lngOffset
  };
}

function formatGpsLocation({ lat, lng }) {
  return `${lat.toFixed(6)},${lng.toFixed(6)}`;
}

const UNAUTHORIZED_ERROR_CODES = new Set(["UNAUTHORIZED"]);
